import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Chip,
  Grid,
  TextField,
  Switch,
  Divider
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getMaintenanceImageSettings, saveMaintenanceImageSettings } from '../../services/maintenanceImageSettingsService';

interface ImageProviderSettingsProps {
  open: boolean;
  onClose: () => void;
  onSave?: (provider: 'imgbb' | 'imagekit' | 'cloudinary') => void;
}

const ImageProviderSettings: React.FC<ImageProviderSettingsProps> = ({
  open,
  onClose,
  onSave
}) => {
  const [selectedProvider, setSelectedProvider] = useState<'imgbb' | 'imagekit' | 'cloudinary'>('imgbb');
  const [compressionEnabled, setCompressionEnabled] = useState(true);
  const [compressionQuality, setCompressionQuality] = useState(80);
  const [maxImageSize, setMaxImageSize] = useState(1920);
  const [autoUpload, setAutoUpload] = useState(true);

  // تحميل الإعدادات المحفوظة من Firebase
  useEffect(() => {
    const loadSettings = async () => {
      try {
        console.log('🔄 تحميل إعدادات صور الصيانة من Firebase...');
        const settings = await getMaintenanceImageSettings();

        setSelectedProvider(settings.provider);
        setCompressionEnabled(settings.compressionEnabled);
        setCompressionQuality(settings.compressionQuality);
        setMaxImageSize(settings.maxImageSize);
        setAutoUpload(settings.autoUpload);

        console.log('✅ تم تحميل الإعدادات:', settings);
      } catch (error) {
        console.error('❌ خطأ في تحميل الإعدادات:', error);
        // استخدام القيم الافتراضية عند الفشل
        setSelectedProvider('imgbb');
        setCompressionEnabled(true);
        setCompressionQuality(80);
        setMaxImageSize(1920);
        setAutoUpload(true);
      }
    };

    if (open) {
      loadSettings();
    }
  }, [open]);

  // حفظ الإعدادات في Firebase
  const handleSave = async () => {
    try {
      console.log('💾 حفظ إعدادات صور الصيانة في Firebase...');

      const settings = {
        provider: selectedProvider,
        compressionEnabled,
        compressionQuality,
        maxImageSize,
        autoUpload,
        fallbackProvider: 'imgbb' as 'imgbb' | 'imagekit' | 'cloudinary',
        retryAttempts: 3
      };

      await saveMaintenanceImageSettings(settings);

      console.log('✅ تم حفظ الإعدادات بنجاح:', settings);

      onSave?.(selectedProvider);
      onClose();
    } catch (error) {
      console.error('❌ خطأ في حفظ الإعدادات:', error);
    }
  };

  const providerInfo = {
    imgbb: {
      name: 'ImgBB',
      description: 'خدمة مجانية لرفع الصور مع روابط دائمة',
      pros: ['مجاني', 'سهل الاستخدام', 'روابط دائمة'],
      cons: ['حد أقصى 32 ميجا', 'لا يوجد إدارة متقدمة'],
      color: 'success'
    },
    imagekit: {
      name: 'ImageKit',
      description: 'خدمة متقدمة لإدارة الصور مع تحسين تلقائي',
      pros: ['تحسين تلقائي', 'CDN سريع', 'إدارة متقدمة'],
      cons: ['يتطلب حساب', 'محدود في النسخة المجانية'],
      color: 'primary'
    },
    cloudinary: {
      name: 'Cloudinary',
      description: 'منصة شاملة لإدارة الوسائط مع ميزات متقدمة',
      pros: ['ميزات متقدمة', 'تحويلات قوية', 'موثوق'],
      cons: ['معقد نسبياً', 'مكلف للاستخدام الكثيف'],
      color: 'secondary'
    }
  } as const;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">إعدادات رفع الصور</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* اختيار مزود الخدمة */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              مزود خدمة رفع الصور
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value as any)}
              >
                {Object.entries(providerInfo).map(([key, info]) => (
                  <Card key={key} sx={{ mb: 2 }}>
                    <CardContent>
                      <FormControlLabel
                        value={key}
                        control={<Radio />}
                        label={
                          <Box>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {info.name}
                              </Typography>
                              <Chip
                                label={key === selectedProvider ? 'مختار' : 'متاح'}
                                color={key === selectedProvider ? info.color : 'default'}
                                size="small"
                                icon={key === selectedProvider ? <CheckIcon /> : undefined}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                              {info.description}
                            </Typography>
                            <Box display="flex" gap={2}>
                              <Box>
                                <Typography variant="caption" color="success.main" fontWeight="bold">
                                  المزايا:
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {info.pros.join(' • ')}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="warning.main" fontWeight="bold">
                                  العيوب:
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {info.cons.join(' • ')}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', width: '100%' }}
                      />
                    </CardContent>
                  </Card>
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* إعدادات الضغط */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              إعدادات ضغط الصور
            </Typography>
            
            <Box mb={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={compressionEnabled}
                    onChange={(e) => setCompressionEnabled(e.target.checked)}
                  />
                }
                label="تفعيل ضغط الصور قبل الرفع"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                يقلل حجم الصور لتوفير مساحة التخزين وتسريع الرفع
              </Typography>
            </Box>

            {compressionEnabled && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="جودة الضغط (%)"
                    type="number"
                    value={compressionQuality}
                    onChange={(e) => setCompressionQuality(Number(e.target.value))}
                    inputProps={{ min: 10, max: 100 }}
                    fullWidth
                    helperText="كلما قل الرقم، قل حجم الملف وقلت الجودة"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="الحد الأقصى للعرض (بكسل)"
                    type="number"
                    value={maxImageSize}
                    onChange={(e) => setMaxImageSize(Number(e.target.value))}
                    inputProps={{ min: 480, max: 4096 }}
                    fullWidth
                    helperText="سيتم تصغير الصور الأكبر من هذا الحد"
                  />
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* إعدادات إضافية */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              إعدادات إضافية
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={autoUpload}
                  onChange={(e) => setAutoUpload(e.target.checked)}
                />
              }
              label="رفع تلقائي للصور بعد التقاطها"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              سيتم رفع الصور تلقائياً بدلاً من انتظار الضغط على زر الحفظ
            </Typography>
          </Grid>

          {/* تحذيرات ونصائح */}
          <Grid item xs={12}>
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                <strong>نصائح:</strong>
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>استخدم ImgBB للاستخدام البسيط والمجاني</li>
                <li>استخدم ImageKit أو Cloudinary للمشاريع الاحترافية</li>
                <li>فعّل الضغط لتوفير مساحة التخزين</li>
                <li>اختبر الإعدادات قبل الاستخدام الفعلي</li>
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<CloudUploadIcon />}
        >
          حفظ الإعدادات
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageProviderSettings;
